import { plainText } from "@/lib/format";
import type { FlexibleTestimonial } from "@/lib/dto";

/** Shared field readers for `FlexibleTestimonial` — the public testimonials
 *  API mixes camelCase/snake_case aliases depending on the call site, so
 *  every card reads through these instead of re-deriving field fallbacks. */

export function testimonialRating(item: FlexibleTestimonial): number | null {
  const rating = item.rating ?? item.review_rating;
  return typeof rating === "number" && Number.isFinite(rating) ? rating : null;
}

export function testimonialName(item: FlexibleTestimonial): string {
  return (
    item.fullName?.trim() ||
    item.full_name?.trim() ||
    item.name?.trim() ||
    "Traveller"
  );
}

export function testimonialTitle(item: FlexibleTestimonial, max = 96): string {
  return (
    plainText(item.reviewTitle || item.review_title, max) ||
    plainText(item.message || item.review_abstract || item.review, max) ||
    "A memorable journey"
  );
}

export function testimonialBody(item: FlexibleTestimonial, max = 90000): string {
  return plainText(
    item.description || item.message || item.review || item.review_abstract,
    max,
  );
}

export function testimonialCountry(item: FlexibleTestimonial): string | null {
  const addr = (item.address || item.location || item.country || item.country_name)?.trim();
  if (!addr) return null;
  const parts = addr.split(",").map((s) => s.trim()).filter(Boolean);
  return parts[parts.length - 1] || null;
}

export function testimonialInitials(name: string | null | undefined): string {
  const cleanName = name?.trim();
  if (!cleanName) return "T";
  return cleanName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}
